import { CommonModule, DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, ViewEncapsulation } from '@angular/core';
import { MetadataAttachment } from '@jms/model';
import { EmptyMessageComponent } from '@jms/ui/ui.empty.message';
import { AccordionComponent, AccordionGroupComponent } from '@workbuddy/ui/components/accordion';
import { AttachmentComponent, type AttachmentSelection } from '@workbuddy/ui/components/attachment';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { injectEntityBoardData } from '../../context';
import { EntityBoardStore } from '../../state';
import { groupBoardAttachments } from './board.attachments.groups';
import { boardAttachmentsBridgeStyles, boardAttachmentsStyleId } from './board.attachments.theme';

/**
 * First-class Attachments body for entity-board ({@link EntityViewDisplay.Attachments}).
 *
 * Same shell as Table/List/Card (header filters, display, bulk, cmdk). Only the body differs:
 * attachment tiles with multi-select checkboxes. Grouping follows owned/system view groupBy.
 */
@Component({
  selector: 'board-attachments',
  templateUrl: './board.attachments.component.html',
  styleUrls: ['./board.attachments.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, AccordionComponent, AccordionGroupComponent, AttachmentComponent, EmptyMessageComponent],
})
export class BoardAttachmentsComponent {

  private readonly document = inject(DOCUMENT);
  private readonly data = injectEntityBoardData<MetadataAttachment>();
  private readonly store = inject(EntityBoardStore);

  public constructor() {

    DesignSystem.Mount(this.document, boardAttachmentsStyleId, boardAttachmentsBridgeStyles);
  }

  protected readonly $items = computed(() => this.data.$items() as readonly MetadataAttachment[]);
  protected readonly $loading = this.data.$loading;
  protected readonly $fetching = this.data.$fetching;
  protected readonly $selectable = computed(() => this.store.$selection()?.type === 'multiple');

  protected readonly $groupBy = computed(() => {
    const view = this.store.$effectiveView();
    const table = view?.table as { groupBy?: string | null } | undefined;
    const list = view?.list as { groupBy?: string | null } | undefined;
    if (table && Object.prototype.hasOwnProperty.call(table, 'groupBy')) return table.groupBy ?? null;
    return list?.groupBy ?? null;
  });

  protected readonly $groups = computed(() => groupBoardAttachments(this.$items(), this.$groupBy()));
  protected readonly $flat = computed(() => !this.$groupBy() || this.$groupBy() === 'none');

  protected readonly $selectedIds = computed(() => new Set(
    (this.store.$selectedItems() as MetadataAttachment[])
      .map(item => item?._id)
      .filter((id): id is string => !!id),
  ));

  protected isSelected(item: MetadataAttachment): boolean {

    return !!item?._id && this.$selectedIds().has(item._id);
  }

  protected onSelectionChanged(event: AttachmentSelection): void {

    if (!event?.attachment?._id) return;

    const selected = this.store.$selectedItems() as MetadataAttachment[];
    const isSelected = selected.some(item => item?._id === event.attachment._id);

    if (event.selected && !isSelected) {
      this.store.setSelectedItems([...selected, event.attachment as MetadataAttachment], true);
      return;
    }

    if (!event.selected && isSelected) {
      this.store.setSelectedItems(selected.filter(item => item?._id !== event.attachment._id), true);
    }
  }
}
